import React, { ReactNode, useEffect, createContext, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { animated, useSpring } from "@react-spring/web";
import { FcTimeline } from "react-icons/fc";
import ScrollContainer from "./ScrollContainer";
import { loginRequest } from "../authConfig";
import { useMsal } from "@azure/msal-react";

const menu = <FcTimeline size={25} />;

// Create a context for managing the list visibility
const CommunicationLayoutContext = createContext({
    showList: true,
    setShowList: (value: boolean) => {},
});

export const useCommunicationLayout = () => useContext(CommunicationLayoutContext);

interface CommunicationLayoutProps {
    list: ReactNode;
    content: ReactNode;
    sidebar?: ReactNode;
}

const CommunicationLayout = ({ list, content, sidebar }: CommunicationLayoutProps) => {
    // Use media query for responsive detection instead of Grommet's ResponsiveContext
    const [isSmall, setIsSmall] = useState(false);
    
    useEffect(() => {
        const checkSize = () => {
            setIsSmall(window.innerWidth < 768); // small breakpoint
        };
        checkSize();
        window.addEventListener("resize", checkSize);
        return () => window.removeEventListener("resize", checkSize);
    }, []);

    const location = useLocation();
    const navigate = useNavigate();

    const [showSidebar, setShowSidebar] = useState(!isSmall && !!sidebar);
    const [showList, setShowList] = useState(true);
    const [showMobileList, setShowMobileList] = useState(false);
    const [showMobileSidebar, setShowMobileSidebar] = useState(false);

    const { width: sideBarWidth, opacity: sideBarOpacity } = useSpring({
        width: showSidebar ? "200px" : "0px",
        opacity: showSidebar ? 1 : 0,
    });

    const { width: listWidth, opacity: listOpacity } = useSpring({
        width: showList ? "400px" : "0px",
        opacity: showList ? 1 : 0,
    });

    useEffect(() => {
        if (!showList) {
            setShowSidebar(false);
        }
    }, [showList]);

    const msalInstance = useMsal();

    const switchOrganization = () => {
        msalInstance.instance.loginRedirect({
            ...loginRequest,
            prompt: "login",
            extraQueryParameters: {
                contextIdentifier: DefaultOrganization,
            },
            tokenQueryParameters: {
                context: DefaultOrganization,
                contextIdentifier: DefaultOrganization,
            },
        });
    };

    return (
        <CommunicationLayoutContext.Provider value={{ showList, setShowList }}>
            <div>
                {/* Header */}
                <div className="flex flex-row items-center gap-2 p-2">
                    <img src="/images/logo.png" alt="Logo" className="w-11 h-11" />
                    <h1 
                        className="text-xl font-bold text-text cursor-pointer"
                        onClick={(e) => e.ctrlKey && switchOrganization()}
                    >
                        Separator Solutions
                    </h1>
                    <button
                        className="p-2 hover:bg-background-back rounded-md transition-colors"
                        onClick={() => setShowList(!showList)}
                    >
                        {menu}
                    </button>
                </div>

                {/* Main Layout */}
                <div className="flex flex-row gap-2 p-2">
                    {!isSmall && (
                        <>
                            {/* Sidebar */}
                            {sidebar && (
                                <animated.div style={{ width: sideBarWidth, opacity: sideBarOpacity, overflow: "hidden" }}>
                                    <div className="min-w-[200px]">{sidebar}</div>
                                </animated.div>
                            )}
                            
                            {/* List Panel */}
                            <animated.div style={{ width: listWidth, opacity: listOpacity, overflow: "hidden" }}>
                                <div className="relative">
                                    <div className="p-2 rounded-md bg-background-front h-[calc(100vh-80px)]">
                                        <ScrollContainer direction="-y">{list}</ScrollContainer>
                                    </div>
                                    {sidebar && (
                                        <button
                                            className="absolute top-0 left-0 p-2 hover:bg-background-back rounded-md transition-colors"
                                            onClick={() => setShowSidebar(!showSidebar)}
                                        >
                                            {menu}
                                        </button>
                                    )}
                                </div>
                            </animated.div>
                        </>
                    )}

                    {/* Content Panel */}
                    <div className="p-2 rounded-md bg-background-front h-[calc(100vh-80px)] w-full">
                        <ScrollContainer direction="-y">{content}</ScrollContainer>
                    </div>
                </div>

                {/* Mobile List Modal */}
                {isSmall && showList && (
                    <div
                        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
                        onClick={() => setShowList(false)}
                    >
                        <div className="bg-background-front rounded-lg w-full max-w-md overflow-hidden">
                            <div className="flex flex-row items-center gap-2 p-2 border-b border-border">
                                <img src="/images/logo.png" alt="Logo" className="w-11 h-11" />
                                <h1 
                                    className="text-xl font-bold text-text cursor-pointer"
                                    onClick={(e) => e.ctrlKey && switchOrganization()}
                                >
                                    Separator Solutions
                                </h1>
                                <button
                                    className="p-2 hover:bg-background-back rounded-md transition-colors ml-auto"
                                    onClick={() => setShowList(false)}
                                >
                                    {menu}
                                </button>
                            </div>
                            <div className="p-2 h-[calc(100vh-200px)] overflow-auto">
                                <ScrollContainer direction="-y">{list}</ScrollContainer>
                            </div>
                        </div>
                    </div>
                )}

                {/* Mobile Sidebar Modal */}
                {isSmall && showSidebar && (
                    <div
                        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
                        onClick={() => setShowSidebar(false)}
                    >
                        <div className="bg-background-front rounded-lg w-full max-w-md h-[calc(100vh-80px)] p-2 overflow-auto">
                            {sidebar}
                        </div>
                    </div>
                )}
            </div>
        </CommunicationLayoutContext.Provider>
    );
};

export default CommunicationLayout;
